export const LIVE_LEDGER_TAB_IDS = {
  ledger: "live-ledger-tab-ledger",
  receiptInbox: "live-ledger-tab-receipt-inbox",
} as const;

export const LIVE_LEDGER_TAB_QUERY_KEY = "tab";

export const LIVE_LEDGER_ACCOUNT_QUERY_KEY = "account";

export const LIVE_LEDGER_CATEGORY_QUERY_KEY = "category";

export const LIVE_LEDGER_AMOUNT_MIN_QUERY_KEY = "amount_min";

export const LIVE_LEDGER_AMOUNT_MAX_QUERY_KEY = "amount_max";

export const LIVE_LEDGER_DATE_FROM_QUERY_KEY = "settled_from";

export const LIVE_LEDGER_DATE_TO_QUERY_KEY = "settled_to";

export const LIVE_LEDGER_TYPE_QUERY_KEY = "type";

export const TYPE_FILTER_OPTIONS = [
  { value: "credit", ftlKey: "live-ledger-filter-type-money-in" },
  { value: "debit", ftlKey: "live-ledger-filter-type-money-out" },
] as const;

export const LIVE_LEDGER_BAI_CODES_QUERY_KEY = "bai_codes";

export const LIVE_LEDGER_METHOD_QUERY_KEY = "method";

export const LIVE_LEDGER_SEARCH_QUERY_KEY = "search";

export const LIVE_LEDGER_PAGE_QUERY_KEY = "page";

export const SEARCH_DEBOUNCE_MS = 300;

export interface DatePreset {
  ftlId: string;
  value: string;
  minDate: () => string;
  maxDate: () => string;
}

/**
 * Format a Date as `YYYY-MM-DD` using the **local** calendar date.
 *
 * `Date.toISOString()` formats in UTC, which shifts the calendar day for users
 * west of UTC after their local midnight crosses the UTC date boundary.
 */
export const toLocalYmd = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const DATE_PRESETS: DatePreset[] = [
  {
    ftlId: "live-ledger-filter-date-last-month",
    value: "last_month",
    minDate: () => {
      const d = new Date();
      d.setMonth(d.getMonth() - 1, 1);
      return toLocalYmd(d);
    },
    maxDate: () => {
      const d = new Date();
      d.setDate(0);
      return toLocalYmd(d);
    },
  },
  {
    ftlId: "live-ledger-filter-date-last-90-days",
    value: "last_90_days",
    minDate: () => {
      const d = new Date();
      d.setDate(d.getDate() - 90);
      return toLocalYmd(d);
    },
    maxDate: () => toLocalYmd(new Date()),
  },
  {
    ftlId: "live-ledger-filter-date-last-12-months",
    value: "last_12_months",
    minDate: () => {
      const d = new Date();
      d.setMonth(d.getMonth() - 12);
      return toLocalYmd(d);
    },
    maxDate: () => toLocalYmd(new Date()),
  },
  {
    ftlId: "live-ledger-filter-date-ytd",
    value: "ytd",
    minDate: () => {
      const d = new Date();
      return `${d.getFullYear()}-01-01`;
    },
    maxDate: () => toLocalYmd(new Date()),
  },
];

export const LIVE_LEDGER_LEDGER_TAB_SLUG = "ledger";
export const LIVE_LEDGER_RECEIPTS_TAB_SLUG = "receipts";

export const LIVE_LEDGER_RECEIPT_BUCKET_QUERY_KEY = "bucket";

export const LIVE_LEDGER_RECEIPT_SORT_QUERY_KEY = "sort";

export const LIVE_LEDGER_RECEIPT_ID_QUERY_KEY = "receiptId";

export const TRANSACTION_ID_PARAM = "transactionId";

export const TRANSACTION_DETAIL_QUERY_KEY = "transactionDetail";

export const TRANSACTION_SUMMARY_QUERY_KEY = "transactionSummary";

// ---------------------------------------------------------------------------
// Export dialog
// ---------------------------------------------------------------------------

export type ExportFormat = "bai2" | "csv" | "zip" | "accounting_software";

export type AccountingFormat = "qbo" | "qfx" | "ofx" | "qif";

export interface ExportDownloadParams {
  format: ExportFormat;
  accountingFormat: AccountingFormat | null;
  fileName: string;
}

export const ACCOUNTING_FORMAT_OPTIONS = [
  { ftlKey: "live-ledger-export-dialog-accounting-format-qbo", value: "qbo" },
  { ftlKey: "live-ledger-export-dialog-accounting-format-qfx", value: "qfx" },
  { ftlKey: "live-ledger-export-dialog-accounting-format-ofx", value: "ofx" },
  { ftlKey: "live-ledger-export-dialog-accounting-format-qif", value: "qif" },
] as const;

export const DEFAULT_ACCOUNTING_FORMAT: AccountingFormat = "qbo";
